import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, CircleDashed, Landmark, Loader2, ShieldAlert } from "lucide-react";

import { paymentFeedApi, paymentFeedQueryKeys } from "../../shared/api/platformBilling";
import {
  platformBankAccountApi,
  platformBankAccountQueryKeys,
  platformSaasMerchantApi,
  platformSaasMerchantQueryKeys,
  type SaasBankAccount,
  type SaasGatewayProvider,
  type SaasMerchantRow,
} from "../../shared/api/platformSaasMerchant";
import { apiError } from "../../shared/api/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";

/** Địa chỉ webhook theo host đang truy cập — không ghi cứng một môi trường nào. */
function webhookUrl(): string {
  const origin =
    typeof window === "undefined" ? "https://his.addy.vn" : window.location.origin;
  return `${origin}/api/v1/public/saas-payment/sepay/webhook`;
}

const PROVIDER_LABEL: Record<SaasGatewayProvider, string> = {
  SEPAY: "SePay",
  PAYOS: "PayOS",
};

/**
 * Nơi tiền phòng khám trả cho Addy chảy về (#18969, #18990).
 *
 * <p>Trang này có <b>hai phần tách hẳn nhau</b>, và sự tách đó là điểm chính:
 *
 * <ul>
 *   <li><b>Tài khoản ngân hàng</b> — Addy nhận tiền vào đâu. Tồn tại độc lập, vì <b>chuyển khoản
 *       tay không đi qua cổng nào</b>. Phòng khám không muốn thanh toán trực tuyến vẫn phải đọc
 *       được số tài khoản. Trước đây ba dòng này nằm trong cấu hình PayOS, nên không khai cổng là
 *       không có số tài khoản — một cách sắp xếp khiến cách trả tiền phổ biến nhất phụ thuộc vào
 *       thứ nó không cần.
 *   <li><b>Cổng thanh toán</b> — chọn MỘT trong các nhà cung cấp, hoặc không dùng cổng nào. Thêm
 *       nhà cung cấp mới sau này chỉ là thêm một lựa chọn ở đây.
 * </ul>
 *
 * <p>Không liên quan tới cổng mà bệnh nhân dùng để trả viện phí cho phòng khám — cái đó mỗi phòng
 * khám tự cấu hình.
 */
export function PlatformSaasMerchantPage() {
  const banks = useQuery({
    queryKey: platformBankAccountQueryKeys.all(),
    queryFn: () => platformBankAccountApi.list(),
  });
  const gateways = useQuery({
    queryKey: platformSaasMerchantQueryKeys.all(),
    queryFn: () => platformSaasMerchantApi.list(),
  });

  if (banks.isLoading || gateways.isLoading) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-7 w-64" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  const bankRows = banks.data ?? [];
  const activeBank = bankRows.find((b) => b.status === "ACTIVE") ?? null;
  const gwRows = gateways.data ?? [];
  const activeGw = gwRows.find((g) => g.status === "ACTIVE") ?? null;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-lg font-bold text-foreground">Thanh toán — tiền phòng khám trả Addy</h1>
        <p className="mt-1 max-w-prose text-sm text-muted-foreground">
          Nạp ví và mua gói thuê bao. Không liên quan tới cổng thanh toán mà bệnh nhân dùng để trả
          viện phí cho phòng khám — cái đó mỗi phòng khám tự cấu hình.
        </p>
      </div>

      {(banks.isError || gateways.isError) && (
        <p className="text-sm text-destructive">
          {apiError(banks.error ?? gateways.error).detail}
        </p>
      )}

      {!activeBank && (
        <div className="flex items-start gap-2 rounded-md border border-amber-600/30 bg-amber-600/5 p-3 text-sm">
          <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
          <div>
            <b>Chưa có tài khoản ngân hàng nào đang dùng.</b> Phòng khám không có chỗ nào để chuyển
            khoản, kể cả chuyển tay.
          </div>
        </div>
      )}

      <BankAccountSection rows={bankRows} active={activeBank} />
      <GatewaySection rows={gwRows} active={activeGw} hasBank={activeBank != null} />
      <VatSection />
    </div>
  );
}

// ═══════════════════════════════════════════════ 1. Tài khoản ngân hàng

function BankAccountSection({
  rows,
  active,
}: {
  rows: SaasBankAccount[];
  active: SaasBankAccount | null;
}) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState<SaasBankAccount | "new" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const invalidate = () =>
    qc.invalidateQueries({ queryKey: platformBankAccountQueryKeys.all() });

  const activate = useMutation({
    mutationFn: (id: string) => platformBankAccountApi.activate(id),
    onSuccess: () => {
      setError(null);
      void invalidate();
    },
    onError: (e: unknown) => setError(apiError(e).detail),
  });

  return (
    <section className="space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="flex items-center gap-1.5 text-base font-semibold text-foreground">
            <Landmark className="h-4 w-4" />
            Tài khoản ngân hàng nhận tiền
          </h2>
          <p className="mt-1 max-w-prose text-sm text-muted-foreground">
            Dùng cho <b>chuyển khoản tay</b> — cách này không cần cổng thanh toán nào. Nếu có bật
            SePay thì chính tài khoản này được dựng thành mã QR, nên số trên QR và số nhân viên đọc
            cho khách luôn là một.
          </p>
        </div>
        <Button size="sm" variant="outline" onClick={() => setEditing("new")}>
          Thêm tài khoản
        </Button>
      </div>

      {error && <p className="text-sm text-destructive">{error}</p>}

      <div className="overflow-x-auto rounded-lg border">
        <table className="w-full min-w-[48rem] text-sm">
          <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              <th className="px-3 py-2 text-left font-medium">Trạng thái</th>
              <th className="px-3 py-2 text-left font-medium">Ngân hàng</th>
              <th className="px-3 py-2 text-left font-medium">Số tài khoản</th>
              <th className="px-3 py-2 text-left font-medium">Chủ tài khoản</th>
              <th className="px-3 py-2" />
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-3 py-6 text-center text-muted-foreground">
                  Chưa khai tài khoản nào.
                </td>
              </tr>
            ) : (
              rows.map((b) => (
                <tr key={b.id} className="border-t">
                  <td className="px-3 py-2">
                    <StatusPill active={b.status === "ACTIVE"} />
                  </td>
                  <td className="px-3 py-2 font-medium text-foreground">{b.bankName}</td>
                  <td className="px-3 py-2 font-mono text-xs">{b.accountNo}</td>
                  <td className="px-3 py-2">{b.accountName ?? "—"}</td>
                  <td className="px-3 py-2">
                    <div className="flex justify-end gap-2">
                      <Button size="sm" variant="ghost" onClick={() => setEditing(b)}>
                        Sửa
                      </Button>
                      {b.status !== "ACTIVE" && (
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={activate.isPending}
                          onClick={() => activate.mutate(b.id)}
                        >
                          Đưa vào dùng
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {active && (
        <p className="text-xs text-muted-foreground">
          Phòng khám đang được hướng dẫn chuyển vào:{" "}
          <b className="text-foreground">
            {active.bankName} · {active.accountNo}
            {active.accountName ? ` · ${active.accountName}` : ""}
          </b>
        </p>
      )}

      {editing && (
        <BankAccountForm
          row={editing === "new" ? null : editing}
          onDone={() => {
            setEditing(null);
            void invalidate();
          }}
          onCancel={() => setEditing(null)}
        />
      )}
    </section>
  );
}

function BankAccountForm({
  row,
  onDone,
  onCancel,
}: {
  row: SaasBankAccount | null;
  onDone: () => void;
  onCancel: () => void;
}) {
  const [bankName, setBankName] = useState(row?.bankName ?? "");
  const [accountNo, setAccountNo] = useState(row?.accountNo ?? "");
  const [accountName, setAccountName] = useState(row?.accountName ?? "");
  const [note, setNote] = useState(row?.note ?? "");
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () => {
      const body = {
        bankName: bankName.trim(),
        accountNo: accountNo.trim(),
        accountName: accountName.trim() || undefined,
        note: note.trim() || undefined,
      };
      return row
        ? platformBankAccountApi.update(row.id, body)
        : platformBankAccountApi.create(body);
    },
    onSuccess: onDone,
    onError: (e: unknown) => setError(apiError(e).detail),
  });

  const valid = bankName.trim() !== "" && accountNo.trim() !== "";

  return (
    <form
      className="space-y-3 rounded-lg border p-4"
      onSubmit={(e) => {
        e.preventDefault();
        if (valid) save.mutate();
      }}
    >
      <h3 className="text-sm font-semibold">
        {row ? "Sửa tài khoản" : "Tài khoản mới"}
      </h3>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Field label="Ngân hàng" hint="Tên ngắn như SePay nhận: MBBank, Vietcombank…">
          <Input value={bankName} onChange={(e) => setBankName(e.target.value)} />
        </Field>
        <Field label="Số tài khoản">
          <Input value={accountNo} onChange={(e) => setAccountNo(e.target.value)} />
        </Field>
        <Field label="Tên chủ tài khoản">
          <Input value={accountName} onChange={(e) => setAccountName(e.target.value)} />
        </Field>
        <Field label="Ghi chú" hint="Chi nhánh, dùng cho khoản gì">
          <Input value={note} onChange={(e) => setNote(e.target.value)} />
        </Field>
      </div>
      {!row && (
        <p className="text-xs text-muted-foreground">
          Tài khoản mới được tạo ở trạng thái <b>Ngừng dùng</b>. Bật là một bước riêng — để một chữ
          số gõ nhầm không lập tức thành nơi khách chuyển tiền vào.
        </p>
      )}
      <div className="flex items-center gap-3">
        <Button type="submit" size="sm" disabled={!valid || save.isPending}>
          {save.isPending ? "Đang lưu…" : "Lưu"}
        </Button>
        <Button type="button" size="sm" variant="ghost" onClick={onCancel}>
          Huỷ
        </Button>
        {error && <span className="text-sm text-destructive">{error}</span>}
      </div>
    </form>
  );
}

// ═══════════════════════════════════════════════ 2. Cổng thanh toán

function GatewaySection({
  rows,
  active,
  hasBank,
}: {
  rows: SaasMerchantRow[];
  active: SaasMerchantRow | null;
  hasBank: boolean;
}) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState<SaasMerchantRow | SaasGatewayProvider | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [check, setCheck] = useState<{ id: string; detail: string } | null>(null);
  const invalidate = () =>
    qc.invalidateQueries({ queryKey: platformSaasMerchantQueryKeys.all() });

  const act = useMutation({
    mutationFn: (v: { id: string; on: boolean }) =>
      v.on ? platformSaasMerchantApi.activate(v.id) : platformSaasMerchantApi.disable(v.id),
    onSuccess: () => {
      setError(null);
      void invalidate();
    },
    onError: (e: unknown) => setError(apiError(e).detail),
  });

  const test = useMutation({
    mutationFn: (id: string) => platformSaasMerchantApi.test(id),
    onSuccess: (r, id) => setCheck({ id, detail: r.detail }),
    onError: (e: unknown, id) => setCheck({ id, detail: apiError(e).detail }),
  });

  const missing = (["SEPAY", "PAYOS"] as SaasGatewayProvider[]).filter(
    (p) => !rows.some((r) => r.provider === p)
  );

  return (
    <section className="space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-foreground">Cổng thanh toán trực tuyến</h2>
          <p className="mt-1 max-w-prose text-sm text-muted-foreground">
            <b>Chỉ một cổng được bật tại một thời điểm.</b> Không bật cổng nào cũng được — khi đó
            phòng khám chuyển khoản tay vào tài khoản phía trên, và kế toán đối chiếu sao kê.
          </p>
        </div>
        <div className="flex gap-2">
          {missing.map((p) => (
            <Button key={p} size="sm" variant="outline" onClick={() => setEditing(p)}>
              Khai {PROVIDER_LABEL[p]}
            </Button>
          ))}
        </div>
      </div>

      {!active && (
        <p className="text-sm text-muted-foreground">
          Hiện <b>không có cổng nào đang dùng</b> — phòng khám không bấm nạp ví hay mua gói trực
          tuyến được, chỉ chuyển khoản tay.
        </p>
      )}
      {error && <p className="text-sm text-destructive">{error}</p>}

      <div className="space-y-3">
        {rows.length === 0 ? (
          <p className="rounded-lg border px-3 py-6 text-center text-sm text-muted-foreground">
            Chưa khai cổng nào.
          </p>
        ) : (
          rows.map((g) => (
            <div key={g.id} className="rounded-lg border p-3">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <StatusPill active={g.status === "ACTIVE"} />
                  <span className="font-semibold text-foreground">
                    {PROVIDER_LABEL[g.provider] ?? g.provider}
                  </span>
                  <GatewaySummary row={g} />
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="ghost" onClick={() => setEditing(g)}>
                    Sửa
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={test.isPending || (g.provider === "SEPAY" && !g.hasChecksumKey)}
                    title={
                      g.provider === "SEPAY" && !g.hasChecksumKey
                        ? "Cần API Token của SePay để tra cứu được"
                        : undefined
                    }
                    onClick={() => test.mutate(g.id)}
                  >
                    {test.isPending && test.variables === g.id ? (
                      <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                    ) : null}
                    Kiểm tra kết nối
                  </Button>
                  <Button
                    size="sm"
                    variant={g.status === "ACTIVE" ? "ghost" : "default"}
                    disabled={act.isPending || (g.provider === "SEPAY" && !hasBank)}
                    title={
                      g.provider === "SEPAY" && !hasBank
                        ? "SePay dựng mã QR từ tài khoản ngân hàng — hãy bật một tài khoản trước"
                        : undefined
                    }
                    onClick={() => act.mutate({ id: g.id, on: g.status !== "ACTIVE" })}
                  >
                    {g.status === "ACTIVE" ? "Ngừng dùng" : "Đưa vào dùng"}
                  </Button>
                </div>
              </div>
              {g.provider === "SEPAY" && !hasBank && (
                <p className="mt-2 text-xs text-amber-700">
                  Chưa có tài khoản ngân hàng đang dùng — SePay không dựng được mã QR.
                </p>
              )}
              {/* Kết quả gắn với ĐÚNG cổng vừa bấm. Một dòng kết quả dùng chung cho mọi cổng thì
                  người đọc không biết nó nói về cái nào. */}
              {check?.id === g.id && (
                <p className="mt-2 text-xs text-muted-foreground">{check.detail}</p>
              )}
            </div>
          ))
        )}
      </div>

      {editing && (
        <GatewayForm
          row={typeof editing === "string" ? null : editing}
          provider={typeof editing === "string" ? editing : editing.provider}
          onDone={() => {
            setEditing(null);
            void invalidate();
          }}
          onCancel={() => setEditing(null)}
        />
      )}
    </section>
  );
}

function GatewaySummary({ row }: { row: SaasMerchantRow }) {
  if (row.provider === "SEPAY") {
    return (
      <span className="text-xs text-muted-foreground">
        tiền tố mã <b className="font-mono">{row.codePrefix ?? "—"}</b> ·{" "}
        {row.hasApiKey ? "khoá webhook ✓" : "khoá webhook ✗"} ·{" "}
        {row.hasChecksumKey ? "API Token ✓" : "API Token ✗"}
      </span>
    );
  }
  return (
    <span className="text-xs text-muted-foreground">
      {row.clientId ?? "—"} · {row.hasApiKey ? "API key ✓" : "API key ✗"} ·{" "}
      {row.hasChecksumKey ? "Checksum ✓" : "Checksum ✗"}
    </span>
  );
}

function GatewayForm({
  row,
  provider,
  onDone,
  onCancel,
}: {
  row: SaasMerchantRow | null;
  provider: SaasGatewayProvider;
  onDone: () => void;
  onCancel: () => void;
}) {
  const [clientId, setClientId] = useState(row?.clientId ?? "");
  const [apiKey, setApiKey] = useState("");
  const [checksumKey, setChecksumKey] = useState("");
  const [baseUrl, setBaseUrl] = useState(row?.baseUrl ?? "");
  const [returnUrl, setReturnUrl] = useState(row?.returnUrl ?? "");
  const [cancelUrl, setCancelUrl] = useState(row?.cancelUrl ?? "");
  const [codePrefix, setCodePrefix] = useState(row?.codePrefix ?? "");
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () => {
      const body = {
        provider,
        clientId: clientId.trim() || undefined,
        apiKey: apiKey.trim() || undefined,
        checksumKey: checksumKey.trim() || undefined,
        baseUrl: baseUrl.trim() || undefined,
        returnUrl: returnUrl.trim() || undefined,
        cancelUrl: cancelUrl.trim() || undefined,
        codePrefix: codePrefix.trim().toUpperCase() || undefined,
      };
      return row
        ? platformSaasMerchantApi.update(row.id, body)
        : platformSaasMerchantApi.create(body);
    },
    onSuccess: onDone,
    onError: (e: unknown) => setError(apiError(e).detail),
  });

  const sepay = provider === "SEPAY";
  const valid = sepay
    ? codePrefix.trim() !== "" && (row != null || apiKey.trim() !== "")
    : row != null || (clientId.trim() !== "" && apiKey.trim() !== "" && checksumKey.trim() !== "");

  return (
    <form
      className="space-y-3 rounded-lg border p-4"
      onSubmit={(e) => {
        e.preventDefault();
        if (valid) save.mutate();
      }}
    >
      <h3 className="text-sm font-semibold">
        {row ? "Sửa" : "Khai"} {PROVIDER_LABEL[provider]}
      </h3>

      {sepay ? (
        <>
          <p className="max-w-prose text-xs text-muted-foreground">
            SePay <b>không có API tạo đơn</b> — nó xem tài khoản ngân hàng ở phần trên và báo khi
            tiền về. Nên ở đây chỉ cần hai thứ, và không cần số tài khoản.
          </p>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field
              label="Tiền tố mã chuyển khoản"
              hint='Phải khớp "Cấu trúc mã thanh toán" khai bên my.sepay.vn. Chỉ chữ HOA và số.'
            >
              <Input
                value={codePrefix}
                onChange={(e) => setCodePrefix(e.target.value.replace(/[^A-Za-z0-9]/g, ""))}
                placeholder="ADDY"
              />
            </Field>
            <Field
              label={row ? "Khoá webhook (để trống = giữ nguyên)" : "Khoá webhook"}
              hint="Chuỗi bạn tự đặt khi tạo webhook trong SePay, kiểu xác thực API Key. SePay dùng nó để GỌI Addy."
            >
              <Input
                type="password"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                autoComplete="new-password"
              />
            </Field>
            <Field
              label={row ? "API Token (để trống = giữ nguyên)" : "API Token"}
              hint="Tạo ở my.sepay.vn. Addy dùng nó để HỎI SePay — tra cứu giao dịch và kiểm tra kết nối. KHÁC khoá webhook."
            >
              <Input
                type="password"
                value={checksumKey}
                onChange={(e) => setChecksumKey(e.target.value)}
                autoComplete="new-password"
              />
            </Field>
          </div>
          <div className="rounded-md border bg-muted/30 p-3 text-xs text-muted-foreground">
            Khai webhook bên my.sepay.vn trỏ về:{" "}
            <code className="break-all font-mono text-foreground">{webhookUrl()}</code> · phương
            thức <b>POST</b> · xác thực <b>API Key</b>.
            <p className="mt-1">
              Địa chỉ này lấy theo host bạn đang mở — nên khi xem qua một đường hầm thử nghiệm
              (ngrok) thì nó là địa chỉ của đường hầm, không phải địa chỉ thật.
            </p>
          </div>
        </>
      ) : (
        <>
          <p className="max-w-prose text-xs text-muted-foreground">
            Lấy ở <b>my.payos.vn → Kênh thanh toán → Thông tin kết nối</b>. Nút Kiểm tra kết nối
            tạo một đơn <b>2.000đ không ai trả</b> rồi bỏ cho hết hạn — PayOS không có cách nào
            khác để chứng minh một bộ khoá ký được.
          </p>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <Field label="Client ID">
              <Input value={clientId} onChange={(e) => setClientId(e.target.value)} />
            </Field>
            <Field label={row ? "API key (để trống = giữ nguyên)" : "API key"}>
              <Input
                type="password"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                autoComplete="new-password"
              />
            </Field>
            <Field label={row ? "Checksum key (để trống = giữ nguyên)" : "Checksum key"}>
              <Input
                type="password"
                value={checksumKey}
                onChange={(e) => setChecksumKey(e.target.value)}
                autoComplete="new-password"
              />
            </Field>
            <Field label="Endpoint PayOS">
              <Input value={baseUrl} onChange={(e) => setBaseUrl(e.target.value)} />
            </Field>
            <Field label="URL khi trả xong">
              <Input value={returnUrl} onChange={(e) => setReturnUrl(e.target.value)} />
            </Field>
            <Field label="URL khi huỷ">
              <Input value={cancelUrl} onChange={(e) => setCancelUrl(e.target.value)} />
            </Field>
          </div>
        </>
      )}

      <div className="flex items-center gap-3">
        <Button type="submit" size="sm" disabled={!valid || save.isPending}>
          {save.isPending ? "Đang lưu…" : "Lưu"}
        </Button>
        <Button type="button" size="sm" variant="ghost" onClick={onCancel}>
          Huỷ
        </Button>
        {error && <span className="text-sm text-destructive">{error}</span>}
      </div>
    </form>
  );
}

// ═══════════════════════════════════════════════ chung

function StatusPill({ active }: { active: boolean }) {
  return active ? (
    <span className="inline-flex items-center gap-1 rounded-full bg-emerald-600/10 px-2 py-0.5 text-xs font-medium text-emerald-700">
      <CheckCircle2 className="h-3 w-3" />
      Đang dùng
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
      <CircleDashed className="h-3 w-3" />
      Ngừng dùng
    </span>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1">
      <div className="text-xs font-medium text-foreground">{label}</div>
      {children}
      {hint && <div className="text-[11px] leading-snug text-muted-foreground">{hint}</div>}
    </div>
  );
}


// ═══════════════════════════════════════════════ 3. Hoá đơn GTGT

/**
 * Addy có xuất hoá đơn GTGT cho phòng khám không (#18990).
 *
 * <p>Tắt <b>không</b> làm mất dấu kỳ nào: hệ thống vẫn mở dòng hoá đơn cho mọi kỳ đã thu tiền, chỉ
 * là không thúc ai đi xuất. Bật lại lúc nào thì hàng đợi vẫn đầy đủ từ đầu — đó là lý do ghi nhận
 * và xuất là hai việc tách rời, chứ không phải một.
 */
function VatSection() {
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);

  const q = useQuery({
    queryKey: paymentFeedQueryKeys.settings(),
    queryFn: () => paymentFeedApi.settings(),
  });

  const toggle = useMutation({
    mutationFn: (on: boolean) => paymentFeedApi.updateSettings({ vatInvoiceEnabled: on }),
    onSuccess: () => {
      setError(null);
      void qc.invalidateQueries({ queryKey: paymentFeedQueryKeys.settings() });
    },
    onError: (e: unknown) => setError(apiError(e).detail),
  });

  const s = q.data;

  return (
    <section className="space-y-3">
      <div>
        <h2 className="text-base font-semibold text-foreground">Hoá đơn GTGT</h2>
        <p className="mt-1 max-w-prose text-sm text-muted-foreground">
          Addy có xuất hoá đơn cho phòng khám hay không. <b>Tắt vẫn ghi nhận đủ</b> — mọi kỳ đã thu
          tiền vẫn có một dòng hoá đơn, chỉ là không ai bị thúc đi xuất. Bật lại lúc nào thì hàng
          đợi vẫn còn nguyên từ đầu.
        </p>
      </div>

      {error && <p className="text-sm text-destructive">{error}</p>}

      <div className="rounded-lg border p-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            {s?.vatInvoiceEnabled ? (
              <span className="inline-flex items-center gap-1 rounded-full bg-emerald-600/10 px-2 py-0.5 text-xs font-medium text-emerald-700">
                <CheckCircle2 className="h-3 w-3" />
                Đang xuất hoá đơn
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
                <CircleDashed className="h-3 w-3" />
                Chỉ ghi nhận, không xuất
              </span>
            )}
            {s?.vatSellerTaxCode && (
              <span className="text-xs text-muted-foreground">
                MST bên bán: <b className="font-mono text-foreground">{s.vatSellerTaxCode}</b>
                {s.vatProvider ? ` · ${s.vatProvider}` : ""}
              </span>
            )}
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="ghost" onClick={() => setEditing((v) => !v)}>
              {editing ? "Đóng" : "Sửa thông tin bên bán"}
            </Button>
            <Button
              size="sm"
              variant={s?.vatInvoiceEnabled ? "ghost" : "default"}
              disabled={toggle.isPending || !s}
              onClick={() => toggle.mutate(!s!.vatInvoiceEnabled)}
            >
              {s?.vatInvoiceEnabled ? "Ngừng xuất" : "Bật xuất hoá đơn"}
            </Button>
          </div>
        </div>
        {!s?.vatInvoiceEnabled && (
          <p className="mt-2 text-xs text-muted-foreground">
            Màn hình <b>Hoá đơn</b> vẫn liệt kê các kỳ đã thu tiền để anh nắm, nhưng không có nút
            ghi số hoá đơn.
          </p>
        )}
      </div>

      {editing && s && <VatForm current={s} onDone={() => { setEditing(false); void qc.invalidateQueries({ queryKey: paymentFeedQueryKeys.settings() }); }} />}
    </section>
  );
}

function VatForm({
  current,
  onDone,
}: {
  current: { vatProvider: string | null; vatSellerTaxCode: string | null; note: string | null };
  onDone: () => void;
}) {
  const [provider, setProvider] = useState(current.vatProvider ?? "");
  const [taxCode, setTaxCode] = useState(current.vatSellerTaxCode ?? "");
  const [note, setNote] = useState(current.note ?? "");
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () =>
      paymentFeedApi.updateSettings({
        vatProvider: provider.trim() || null,
        vatSellerTaxCode: taxCode.trim() || null,
        note: note.trim() || null,
      }),
    onSuccess: onDone,
    onError: (e: unknown) => setError(apiError(e).detail),
  });

  return (
    <form
      className="space-y-3 rounded-lg border p-4"
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate();
      }}
    >
      <h3 className="text-sm font-semibold">Thông tin bên bán (Addy)</h3>
      <div className="grid gap-3 sm:grid-cols-3">
        <Field label="Mã số thuế Addy" hint="In lên hoá đơn ở ô bên bán.">
          <Input value={taxCode} onChange={(e) => setTaxCode(e.target.value)} />
        </Field>
        <Field label="Nhà cung cấp hoá đơn" hint="Ví dụ: PA_VIETNAM, VIETTEL, MISA.">
          <Input value={provider} onChange={(e) => setProvider(e.target.value)} />
        </Field>
        <Field label="Ghi chú">
          <Input value={note} onChange={(e) => setNote(e.target.value)} />
        </Field>
      </div>
      <p className="max-w-prose text-xs text-muted-foreground">
        Khai ở đây <b>chưa làm Addy tự xuất hoá đơn</b> — phần nối với nhà cung cấp chưa làm. Hôm
        nay kế toán xuất trên cổng của họ rồi ghi số về màn hình Hoá đơn.
      </p>
      <div className="flex items-center gap-3">
        <Button type="submit" size="sm" disabled={save.isPending}>
          {save.isPending ? "Đang lưu…" : "Lưu"}
        </Button>
        {error && <span className="text-sm text-destructive">{error}</span>}
      </div>
    </form>
  );
}
